import { useState, useEffect, useCallback } from 'react';
import './AdminProgramManagement.css'; // Reuse existing styles
import config from '../config';

const { API_URL } = config;

const AdminEnrollmentManagement = ({ user, onMessage, onEnrollmentUpdate, refreshEnrollments }) => {
  const [enrollments, setEnrollments] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [attempts, setAttempts] = useState({});  // Store attempts by user_email
  const [loading, setLoading] = useState(false);
  const [filterEmail, setFilterEmail] = useState('');
  const [filterProgram, setFilterProgram] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [uploadingCert, setUploadingCert] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  // Load attempts for enrolled users (fetches attempt history for display)
  const loadAttempts = useCallback(async (enrollmentsList) => {
    try {
      const attemptsMap = {};
      
      // Get unique user emails
      const userEmails = [...new Set(enrollmentsList.map(e => e.user_email))];
      
      // Load attempts for each user
      for (const email of userEmails) {
        try {
          const response = await fetch(`${API_URL}/admin/user-attempts?user_email=${encodeURIComponent(email)}`, {
            headers: {
              ...(user?.token && user.token !== 'demo-token-for-local-testing' ? { 'Authorization': `Bearer ${user.token}` } : {})
            }
          });
          
          if (response.ok) {
            const userAttempts = await response.json();
            attemptsMap[email] = userAttempts;
          }
        } catch (error) {
          console.error(`Error loading attempts for ${email}:`, error);
        }
      }
      
      setAttempts(attemptsMap);
    } catch (error) {
      console.error('Error loading attempts:', error);
    }
  }, [user?.token]);

  // Load enrollments from API
  const loadEnrollments = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterEmail) params.append('user_email', filterEmail);
      if (filterProgram) params.append('program_name', filterProgram);
      if (filterStatus) params.append('status', filterStatus);

      const url = `${API_URL}/admin/enrollments${params.toString() ? '?' + params.toString() : ''}`;
      const response = await fetch(url, {
        headers: {
          ...(user?.token && user.token !== 'demo-token-for-local-testing' ? { 'Authorization': `Bearer ${user.token}` } : {})
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          setEnrollments([]);
          return;
        }
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      // Sort by enrollment date descending
      const sortedEnrollments = data.sort((a, b) => new Date(b.enrollment_date) - new Date(a.enrollment_date));
      setEnrollments(sortedEnrollments);
      
      // Load attempts for all enrolled users
      await loadAttempts(sortedEnrollments);
    } catch (error) {
      console.error('Error loading enrollments:', error);
      
      // Demo mode - provide sample enrollments
      if (user?.token === 'demo-token-for-local-testing') {
        const demoEnrollments = [
          {
            user_email: 'john.doe@example.com',
            program_name: 'Cloud Operator Demo',
            status: 'active',
            enrollment_date: new Date().toISOString(),
            last_activity: new Date().toISOString()
          },
          {
            user_email: 'jane.smith@example.com',
            program_name: 'DevOps Fundamentals Demo',
            status: 'active',
            enrollment_date: new Date(Date.now() - 86400000).toISOString(), // Yesterday
            last_activity: new Date(Date.now() - 3600000).toISOString() // 1 hour ago
          },
          {
            user_email: 'demo@example.com',
            program_name: 'AWS Basics Demo',
            status: 'completed',
            enrollment_date: new Date(Date.now() - 172800000).toISOString(), // 2 days ago
            completion_date: new Date(Date.now() - 86400000).toISOString()
          }
        ];
        setEnrollments(demoEnrollments);
      } else {
        onMessage({ text: 'Error loading enrollments: ' + error.message, type: 'error' });
        setEnrollments([]);
      }
    } finally {
      setLoading(false);
    }
  }, [filterEmail, filterProgram, filterStatus, user?.token, onMessage, loadAttempts]);

  // Reset user attempt
  const handleResetAttempt = async (userEmail, assessmentName) => {
    if (!window.confirm(`Reset attempt for ${userEmail} on ${assessmentName}?\n\nThis will delete their attempt and allow them to retake the exam.`)) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/admin/reset-attempt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(user?.token ? { 'Authorization': `Bearer ${user.token}` } : {})
        },
        body: JSON.stringify({
          user_email: userEmail,
          assessment_name: assessmentName
        })
      });

      const result = await response.json();

      if (response.ok) {
        console.log('Reset attempt result:', result);
        const deletedCount = result.deleted_count || 0;
        onMessage({ 
          text: `✓ Attempt reset successfully! Deleted ${deletedCount} record(s). ⚠️ IMPORTANT: Tell the user to REFRESH their browser (F5) to see the "Take Assessment" button.`, 
          type: 'success' 
        });
        // Reload enrollments and attempts
        await loadEnrollments();
      } else {
        onMessage({ text: 'Error: ' + (result.error || 'Unknown error'), type: 'error' });
      }
    } catch (error) {
      console.error('Error resetting attempt:', error);
      onMessage({ text: 'Error: ' + error.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Load programs for filter dropdown
  const loadPrograms = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/admin/programs`, {
        headers: {
          ...(user?.token && user.token !== 'demo-token-for-local-testing' ? { 'Authorization': `Bearer ${user.token}` } : {})
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          setPrograms([]);
          return;
        }
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setPrograms(data || []);
    } catch (error) {
      console.error('Error loading programs:', error);
      
      // Demo mode - provide sample programs
      if (user?.token === 'demo-token-for-local-testing') {
        const demoPrograms = [
          { name: 'Cloud Operator Demo', title: 'Cloud Operator Demo' },
          { name: 'DevOps Fundamentals Demo', title: 'DevOps Fundamentals Demo' },
          { name: 'AWS Basics Demo', title: 'AWS Basics Demo' }
        ];
        setPrograms(demoPrograms);
      } else {
        setPrograms([]);
      }
    }
  }, [user?.token]);

  useEffect(() => {
    loadPrograms();
    loadEnrollments();
  }, [loadPrograms, loadEnrollments]);

  // Handle unenrollment
  const handleUnenroll = async (userEmail, programName) => {
    const confirmMessage = `Are you sure you want to unenroll ${userEmail} from "${programName}"?\n\n` +
      `This will:\n` +
      `• Set their enrollment status to 'inactive'\n` +
      `• Send them an unenrollment notification email\n` +
      `• Remove their access to the program\n\n` +
      `This action can be reversed by re-enrolling them.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setLoading(true);
    try {
      // Demo mode - simulate unenrollment
      if (user?.token === 'demo-token-for-local-testing') {
        // Update shared state for demo
        if (onEnrollmentUpdate) {
          onEnrollmentUpdate(userEmail, programName, 'inactive');
        }
        // Update local state for demo
        setEnrollments(prev => prev.map(enrollment => 
          enrollment.user_email === userEmail && enrollment.program_name === programName
            ? { ...enrollment, status: 'inactive', unenrollment_date: new Date().toISOString() }
            : enrollment
        ));
        onMessage({ 
          text: `Demo: Successfully unenrolled ${userEmail} from ${programName}. In production, they would receive an email notification.`, 
          type: 'success' 
        });
        setLoading(false);
        return;
      }

      // Fallback to existing endpoint until new infrastructure is deployed
      const response = await fetch(`${API_URL}/admin/enrollments`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(user?.token ? { 'Authorization': `Bearer ${user.token}` } : {})
        },
        body: JSON.stringify({
          user_email: userEmail,
          program_name: programName,
          action: 'unenroll',
          reason: 'Admin unenrollment'
        })
      });

      const result = await response.json();

      if (response.ok) {
        // Update shared state
        if (onEnrollmentUpdate) {
          onEnrollmentUpdate(userEmail, programName, 'inactive');
        }
        onMessage({ 
          text: `Successfully unenrolled ${userEmail} from ${programName}. Notification email sent.`, 
          type: 'success' 
        });
        loadEnrollments(); // Reload the list
        // Also refresh the shared enrollments
        if (refreshEnrollments) {
          refreshEnrollments();
        }
      } else {
        onMessage({ text: 'Error: ' + (result.error || 'Unknown error'), type: 'error' });
      }
    } catch (error) {
      console.error('Error unenrolling user:', error);
      onMessage({ text: 'Error: ' + error.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Handle re-enrollment (reactivate inactive enrollment)
  const handleReenroll = async (userEmail, programName) => {
    const confirmMessage = `Re-enroll ${userEmail} in "${programName}"?\n\n` +
      `This will:\n` +
      `• Reactivate their enrollment\n` +
      `• Send them a re-enrollment notification email\n` +
      `• Restore their access to the program`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setLoading(true);
    try {
      // Demo mode - simulate re-enrollment
      if (user?.token === 'demo-token-for-local-testing') {
        // Update shared state for demo
        if (onEnrollmentUpdate) {
          onEnrollmentUpdate(userEmail, programName, 'active');
        }
        // Update local state for demo
        setEnrollments(prev => prev.map(enrollment => 
          enrollment.user_email === userEmail && enrollment.program_name === programName
            ? { ...enrollment, status: 'active', unenrollment_date: null, last_activity: new Date().toISOString() }
            : enrollment
        ));
        onMessage({ 
          text: `Demo: Successfully re-enrolled ${userEmail} in ${programName}. In production, they would receive an email notification.`, 
          type: 'success' 
        });
        setLoading(false);
        return;
      }

      // Fallback to existing endpoint until new infrastructure is deployed
      const response = await fetch(`${API_URL}/admin/enrollments`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(user?.token ? { 'Authorization': `Bearer ${user.token}` } : {})
        },
        body: JSON.stringify({
          user_email: userEmail,
          program_name: programName,
          action: 'reenroll'
        })
      });

      const result = await response.json();

      if (response.ok) {
        // Update shared state
        if (onEnrollmentUpdate) {
          onEnrollmentUpdate(userEmail, programName, 'active');
        }
        onMessage({ 
          text: `Successfully re-enrolled ${userEmail} in ${programName}. Notification email sent.`, 
          type: 'success' 
        });
        loadEnrollments(); // Reload the list
        // Also refresh the shared enrollments
        if (refreshEnrollments) {
          refreshEnrollments();
        }
      } else {
        onMessage({ text: 'Error: ' + (result.error || 'Unknown error'), type: 'error' });
      }
    } catch (error) {
      console.error('Error re-enrolling user:', error);
      onMessage({ text: 'Error: ' + error.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Handle certificate file selection
  const handleFileSelect = (event, userEmail, programName) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg'];
    if (!allowedTypes.includes(file.type)) {
      onMessage({ text: 'Only PDF, PNG or JPG files are allowed', type: 'error' });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      onMessage({ text: 'File size must be less than 5MB', type: 'error' });
      return;
    }

    setSelectedFile({ file, userEmail, programName });
  };

  // Handle certificate upload
  const handleUploadCertificate = async (userEmail, programName) => {
    if (!selectedFile || selectedFile.userEmail !== userEmail || selectedFile.programName !== programName) {
      onMessage({ text: 'Please select a file first', type: 'error' });
      return;
    }

    setUploadingCert(`${userEmail}-${programName}`);
    try {
      // Demo mode - simulate upload
      if (user?.token === 'demo-token-for-local-testing') {
        await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate upload delay
        
        setEnrollments(prev => prev.map(enrollment => 
          enrollment.user_email === userEmail && enrollment.program_name === programName
            ? { 
                ...enrollment, 
                prerequisites: {
                  aws_cloud_practitioner: {
                    status: 'uploaded',
                    upload_date: new Date().toISOString(),
                    verified: false
                  }
                }
              }
            : enrollment
        ));
        
        onMessage({ 
          text: `Demo: Certificate uploaded for ${userEmail}. In production, this would be stored in S3.`, 
          type: 'success' 
        });
        setSelectedFile(null);
        setUploadingCert(null);
        return;
      }

      // Read file as base64
      const fileReader = new FileReader();
      
      const fileBase64 = await new Promise((resolve, reject) => {
        fileReader.onload = (e) => resolve(e.target.result.split(',')[1]); // Remove data:...;base64, prefix
        fileReader.onerror = reject;
        fileReader.readAsDataURL(selectedFile.file);
      });

      // Send as JSON with base64-encoded file
      const payload = {
        user_email: userEmail,
        program_name: programName,
        prerequisite_type: 'aws_cloud_practitioner',
        file_content: fileBase64,
        file_name: selectedFile.file.name
      };

      const response = await fetch(`${API_URL}/admin/enrollments/certificate/upload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(user?.token ? { 'Authorization': `Bearer ${user.token}` } : {})
        },
        body: JSON.stringify(payload)
      });

      let result;
      try {
        result = await response.json();
      } catch (jsonError) {
        const text = await response.text();
        console.error('Failed to parse JSON response:', text);
        throw new Error(`Server returned invalid response: ${text.substring(0, 100)}`);
      }

      if (response.ok) {
        onMessage({ text: 'Certificate uploaded successfully', type: 'success' });
        setSelectedFile(null);
        loadEnrollments(); // Reload to show updated status
      } else {
        onMessage({ text: 'Error: ' + (result.error || 'Upload failed'), type: 'error' });
      }
    } catch (error) {
      console.error('Error uploading certificate:', error);
      onMessage({ text: 'Error: ' + error.message, type: 'error' });
    } finally {
      setUploadingCert(null);
    }
  };

  // Handle certificate verification toggle
  const handleToggleVerification = async (userEmail, programName, currentStatus) => {
    const newStatus = !currentStatus;
    const action = newStatus ? 'verify' : 'unverify';
    
    if (!window.confirm(`${action === 'verify' ? 'Verify' : 'Unverify'} AWS Cloud Practitioner certificate for ${userEmail}?`)) {
      return;
    }

    setLoading(true);
    try {
      // Demo mode - simulate verification
      if (user?.token === 'demo-token-for-local-testing') {
        setEnrollments(prev => prev.map(enrollment => 
          enrollment.user_email === userEmail && enrollment.program_name === programName
            ? { 
                ...enrollment, 
                prerequisites: {
                  ...enrollment.prerequisites,
                  aws_cloud_practitioner: {
                    ...enrollment.prerequisites?.aws_cloud_practitioner,
                    verified: newStatus,
                    verified_date: newStatus ? new Date().toISOString() : null,
                    verified_by: newStatus ? user.email : null
                  }
                }
              }
            : enrollment
        ));
        
        onMessage({ 
          text: `Demo: Certificate ${action}ed for ${userEmail}. In production, user would receive an email notification.`, 
          type: 'success' 
        });
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/admin/enrollments/certificate/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(user?.token ? { 'Authorization': `Bearer ${user.token}` } : {})
        },
        body: JSON.stringify({
          user_email: userEmail,
          program_name: programName,
          prerequisite_type: 'aws_cloud_practitioner',
          verified: newStatus
        })
      });

      const result = await response.json();

      if (response.ok) {
        onMessage({ text: `Certificate ${action}ed successfully`, type: 'success' });
        loadEnrollments(); // Reload to show updated status
      } else {
        onMessage({ text: 'Error: ' + (result.error || 'Verification failed'), type: 'error' });
      }
    } catch (error) {
      console.error('Error toggling verification:', error);
      onMessage({ text: 'Error: ' + error.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Handle view certificate
  const handleViewCertificate = async (userEmail, programName) => {
    try {
      const response = await fetch(
        `${API_URL}/admin/enrollments/certificate?user_email=${encodeURIComponent(userEmail)}&program_name=${encodeURIComponent(programName)}&type=aws_cloud_practitioner`,
        {
          method: 'GET',
          headers: {
            ...(user?.token ? { 'Authorization': `Bearer ${user.token}` } : {})
          }
        }
      );

      const result = await response.json();

      if (response.ok && result.download_url) {
        // Open certificate in new tab
        window.open(result.download_url, '_blank');
      } else {
        onMessage({ text: 'Error: ' + (result.error || 'Failed to get certificate URL'), type: 'error' });
      }
    } catch (error) {
      console.error('Error viewing certificate:', error);
      onMessage({ text: 'Error: ' + error.message, type: 'error' });
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch {
      return 'Unknown';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return '#28a745';
      case 'completed': return '#007bff';
      case 'inactive': return '#6c757d';
      default: return '#6c757d';
    }
  };

  // Filter enrollments based on current filters
  const filteredEnrollments = enrollments.filter(enrollment => {
    const emailMatch = !filterEmail || enrollment.user_email.toLowerCase().includes(filterEmail.toLowerCase());
    const programMatch = !filterProgram || enrollment.program_name === filterProgram;
    const statusMatch = !filterStatus || enrollment.status === filterStatus;
    return emailMatch && programMatch && statusMatch;
  });

  return (
    <div className="admin-program-management">
      {/* Demo Mode Notice */}
      {user?.token === 'demo-token-for-local-testing' && (
        <div className="card" style={{background: '#d1ecf1', border: '1px solid #bee5eb'}}>
          <h4 style={{color: '#0c5460'}}>🔧 Demo Mode Active</h4>
          <p style={{color: '#0c5460', margin: '10px 0'}}>
            You're viewing demo enrollment data. In production, this would connect to your actual enrollment database 
            and send real email notifications when users are unenrolled or re-enrolled.
          </p>
        </div>
      )}

      {/* Filters */}
      <div className="card">
        <h3>Filter Enrollments</h3>
        <div className="form-row">
          <div className="form-group">
            <label>User Email:</label>
            <input
              type="email"
              value={filterEmail}
              onChange={(e) => setFilterEmail(e.target.value)}
              placeholder="Filter by user email"
              className="form-control"
            />
          </div>
          <div className="form-group">
            <label>Program:</label>
            <select
              value={filterProgram}
              onChange={(e) => setFilterProgram(e.target.value)}
              className="form-control"
            >
              <option value="">All Programs</option>
              {programs.map(program => (
                <option key={program.name} value={program.name}>
                  {program.title || program.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Status:</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="form-control"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
        <div className="form-actions">
          <button 
            className="btn btn-primary" 
            onClick={loadEnrollments}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Apply Filters'}
          </button>
          <button 
            className="btn btn-secondary" 
            onClick={() => {
              setFilterEmail('');
              setFilterProgram('');
              setFilterStatus('');
            }}
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Enrollments List */}
      <div className="card">
        <div className="card-header">
          <h3>User Enrollments ({filteredEnrollments.length} total)</h3>
          <button 
            className="btn btn-secondary" 
            onClick={loadEnrollments}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>

        {loading && <div className="loading">Loading enrollments...</div>}

        {!loading && filteredEnrollments.length === 0 && (
          <p>No enrollments found matching the current filters.</p>
        )}

        {!loading && filteredEnrollments.length > 0 && (
          <div className="programs-list">
            {filteredEnrollments.map((enrollment, index) => (
              <div key={`${enrollment.user_email}-${enrollment.program_name}-${index}`} className="program-item">
                <div className="program-header">
                  <div>
                    <h4>{enrollment.user_email}</h4>
                    <p style={{margin: '5px 0', color: '#666'}}>
                      Program: <strong>{enrollment.program_name}</strong>
                    </p>
                  </div>
                  <div className="program-actions">
                    {enrollment.status === 'active' && (
                      <button 
                        className="btn btn-danger" 
                        onClick={() => handleUnenroll(enrollment.user_email, enrollment.program_name)}
                        disabled={loading}
                      >
                        Unenroll User
                      </button>
                    )}
                    {enrollment.status === 'inactive' && (
                      <button 
                        className="btn btn-primary" 
                        onClick={() => handleReenroll(enrollment.user_email, enrollment.program_name)}
                        disabled={loading}
                      >
                        Re-enroll User
                      </button>
                    )}
                    {enrollment.status === 'completed' && (
                      <span className="btn btn-secondary" style={{cursor: 'default'}}>
                        Program Completed
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="program-details">
                  <p><strong>Status:</strong> 
                    <span style={{
                      color: getStatusColor(enrollment.status),
                      fontWeight: 'bold',
                      textTransform: 'uppercase',
                      marginLeft: '8px'
                    }}>
                      {enrollment.status}
                    </span>
                  </p>
                  <p><strong>Enrolled:</strong> {formatDate(enrollment.enrollment_date)}</p>
                  
                  {enrollment.last_activity && (
                    <p><strong>Last Activity:</strong> {formatDate(enrollment.last_activity)}</p>
                  )}
                  
                  {enrollment.completion_date && (
                    <p><strong>Completed:</strong> {formatDate(enrollment.completion_date)}</p>
                  )}
                  
                  {enrollment.unenrollment_date && (
                    <p><strong>Unenrolled:</strong> {formatDate(enrollment.unenrollment_date)}</p>
                  )}
                  
                  {/* Prerequisites Section */}
                  <div style={{
                    marginTop: '15px',
                    padding: '15px',
                    background: '#f8f9fa',
                    borderRadius: '5px',
                    border: '1px solid #dee2e6'
                  }}>
                    <h5 style={{margin: '0 0 10px 0', fontSize: '0.95em'}}>📋 Prerequisites & Exam Status</h5>
                    
                    {/* Exam Eligibility Status */}
                    <div style={{marginBottom: '15px', padding: '10px', background: '#fff', borderRadius: '4px', border: '1px solid #dee2e6'}}>
                      <strong>Exam Eligibility:</strong>
                      {(() => {
                        const prereq = enrollment.prerequisites?.aws_cloud_practitioner;
                        const canTakeExam = prereq?.verified === true;
                        
                        return (
                          <div style={{marginTop: '8px'}}>
                            <span style={{
                              padding: '6px 12px',
                              borderRadius: '4px',
                              fontSize: '0.9em',
                              fontWeight: 'bold',
                              background: canTakeExam ? '#d4edda' : '#f8d7da',
                              color: canTakeExam ? '#155724' : '#721c24',
                              display: 'inline-block'
                            }}>
                              {canTakeExam ? '✓ Eligible to Take Exam' : '✗ Not Eligible - Prerequisites Required'}
                            </span>
                            {!canTakeExam && (
                              <p style={{fontSize: '0.85em', color: '#666', marginTop: '8px', marginBottom: 0}}>
                                User must upload and have their AWS Cloud Practitioner certificate verified before taking the exam.
                              </p>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                    
                    {/* AWS Cloud Practitioner Certificate */}
                    <div style={{marginBottom: '10px'}}>
                      <strong>AWS Cloud Practitioner:</strong>
                      {enrollment.prerequisites?.aws_cloud_practitioner ? (
                        <div style={{marginTop: '8px'}}>
                          <div style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px'}}>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '0.85em',
                              fontWeight: 'bold',
                              background: enrollment.prerequisites.aws_cloud_practitioner.verified ? '#d4edda' : '#fff3cd',
                              color: enrollment.prerequisites.aws_cloud_practitioner.verified ? '#155724' : '#856404'
                            }}>
                              {enrollment.prerequisites.aws_cloud_practitioner.verified ? '✓ Verified' : '⏳ Pending Verification'}
                            </span>
                            {enrollment.prerequisites.aws_cloud_practitioner.upload_date && (
                              <span style={{fontSize: '0.85em', color: '#666'}}>
                                Uploaded: {formatDate(enrollment.prerequisites.aws_cloud_practitioner.upload_date)}
                              </span>
                            )}
                          </div>
                          
                          {/* Verification Checkbox */}
                          <label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer'}}>
                            <input
                              type="checkbox"
                              checked={enrollment.prerequisites.aws_cloud_practitioner.verified || false}
                              onChange={() => handleToggleVerification(
                                enrollment.user_email, 
                                enrollment.program_name,
                                enrollment.prerequisites.aws_cloud_practitioner.verified
                              )}
                              disabled={loading}
                              style={{width: '18px', height: '18px', cursor: 'pointer'}}
                            />
                            <span style={{fontSize: '0.9em'}}>
                              Certificate verified and valid
                            </span>
                          </label>
                          
                          {/* View Certificate Button */}
                          <button
                            className="btn btn-secondary"
                            onClick={() => handleViewCertificate(enrollment.user_email, enrollment.program_name)}
                            style={{fontSize: '0.85em', padding: '6px 12px', marginTop: '8px'}}
                          >
                            📄 View Certificate
                          </button>
                        </div>
                      ) : (
                        <div style={{marginTop: '8px'}}>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '0.85em',
                            fontWeight: 'bold',
                            background: '#f8d7da',
                            color: '#721c24',
                            display: 'inline-block',
                            marginBottom: '10px'
                          }}>
                            ✗ Not Uploaded
                          </span>
                          
                          {/* File Upload */}
                          <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
                            <input
                              type="file"
                              accept=".pdf,.png,.jpg,.jpeg"
                              onChange={(e) => handleFileSelect(e, enrollment.user_email, enrollment.program_name)}
                              style={{fontSize: '0.9em'}}
                              id={`cert-upload-${enrollment.user_email}-${enrollment.program_name}`}
                            />
                            <button
                              className="btn btn-primary"
                              onClick={() => handleUploadCertificate(enrollment.user_email, enrollment.program_name)}
                              disabled={
                                uploadingCert === `${enrollment.user_email}-${enrollment.program_name}` ||
                                !selectedFile ||
                                selectedFile.userEmail !== enrollment.user_email ||
                                selectedFile.programName !== enrollment.program_name
                              }
                              style={{fontSize: '0.85em', padding: '6px 12px'}}
                            >
                              {uploadingCert === `${enrollment.user_email}-${enrollment.program_name}` ? 'Uploading...' : 'Upload'}
                            </button>
                          </div>
                          <p style={{fontSize: '0.8em', color: '#666', margin: '5px 0 0 0'}}>
                            PDF only, max 5MB
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Exam Eligibility Status */}
                  <div style={{
                    marginTop: '15px',
                    padding: '15px',
                    background: '#fff',
                    borderRadius: '5px',
                    border: '1px solid #dee2e6'
                  }}>
                    <h5 style={{margin: '0 0 10px 0', fontSize: '0.95em'}}>🎯 Can Take Exam?</h5>
                    {(() => {
                      const userAttempts = attempts[enrollment.user_email] || [];
                      const programAttempt = userAttempts.find(a => a.assessment_name === enrollment.program_name);
                      const hasPrerequisites = enrollment.prerequisites?.aws_cloud_practitioner?.verified;
                      const hasAttempt = !!programAttempt;
                      const canTakeExam = hasPrerequisites && !hasAttempt;
                      
                      return (
                        <div>
                          <div style={{marginBottom: '10px'}}>
                            <span style={{
                              padding: '8px 16px',
                              borderRadius: '4px',
                              fontSize: '1em',
                              fontWeight: 'bold',
                              background: canTakeExam ? '#d4edda' : '#fff3cd',
                              color: canTakeExam ? '#155724' : '#856404',
                              display: 'inline-block'
                            }}>
                              {canTakeExam ? '✓ YES - Can Take Exam' : '✗ NO - Cannot Take Exam'}
                            </span>
                          </div>
                          
                          {/* Reason */}
                          <div style={{fontSize: '0.9em', color: '#666', marginTop: '10px'}}>
                            {!hasPrerequisites && (
                              <div style={{marginBottom: '5px'}}>❌ Prerequisites not verified</div>
                            )}
                            {hasAttempt && (
                              <div style={{marginBottom: '5px'}}>
                                ❌ Already attempted ({programAttempt.passed ? 'Passed' : 'Failed'} - {programAttempt.score}%)
                              </div>
                            )}
                            {canTakeExam && (
                              <div>✅ Prerequisites verified and no previous attempt</div>
                            )}
                          </div>
                          
                          {/* Reset Button if has attempt */}
                          {hasAttempt && (
                            <div style={{marginTop: '15px'}}>
                              <button
                                className="btn btn-warning"
                                onClick={() => handleResetAttempt(enrollment.user_email, enrollment.program_name)}
                                disabled={loading}
                                style={{fontSize: '0.9em', padding: '8px 16px'}}
                              >
                                🔄 Reset Attempt (Allow Retake)
                              </button>
                              <p style={{fontSize: '0.8em', color: '#666', marginTop: '5px', marginBottom: 0}}>
                                This will delete the attempt. After reset, tell the user to <strong>refresh their browser (F5)</strong> to see the "Take Assessment" button.
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Information Card */}
      <div className="card" style={{background: '#f8f9fa', border: '1px solid #dee2e6'}}>
        <h4 style={{color: '#495057'}}>📧 About Enrollment Management</h4>
        <div style={{color: '#495057', margin: '10px 0'}}>
          <p><strong>Unenroll:</strong> Removes user access and sends notification email. Can be reversed.</p>
          <p><strong>Re-enroll:</strong> Reactivates inactive enrollments and sends welcome back email.</p>
          <p><strong>Email Notifications:</strong> Users receive automatic emails when their enrollment status changes.</p>
          <p><strong>Prerequisites:</strong> Upload and verify AWS Cloud Practitioner certificates for users. Users cannot start exams until verified.</p>
          <p><strong>Attempt Reset:</strong> Deletes the user's exam attempt, allowing them to retake it. The user must refresh their browser to see the change.</p>
          <p><strong>Status Types:</strong></p>
          <ul>
            <li><strong style={{color: '#28a745'}}>Active:</strong> User is currently enrolled and can access the program</li>
            <li><strong style={{color: '#007bff'}}>Completed:</strong> User has successfully completed the program</li>
            <li><strong style={{color: '#6c757d'}}>Inactive:</strong> User has been unenrolled or enrollment expired</li>
          </ul>
          <p><strong>Certificate Status:</strong></p>
          <ul>
            <li><strong style={{color: '#155724'}}>✓ Verified:</strong> Certificate uploaded and verified - user can start exam</li>
            <li><strong style={{color: '#856404'}}>⏳ Pending:</strong> Certificate uploaded but not yet verified</li>
            <li><strong style={{color: '#721c24'}}>✗ Not Uploaded:</strong> No certificate on file - user cannot start exam</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminEnrollmentManagement;